import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Autocomplete,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  Menu,
  Divider,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import UpdateIcon from '@mui/icons-material/Update'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import BlockIcon from '@mui/icons-material/Block'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import NoteIcon from '@mui/icons-material/Note'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import SaveIcon from '@mui/icons-material/Save'
import { supabase, functionAuthHeaders } from '../../config/supabase'
import { ENV } from '../../config/env'
import type { ServicesTemplate } from '../../types'

const DAILY_EMAIL_LIMIT = 100

interface EntityOption {
  id: string
  name: string
  slug?: string
  email?: string
  city?: string
  phone?: string
  address?: string
  type: 'chain' | 'undertaker'
  last_email_sent_at?: string
}

interface SubmissionToken {
  id: string
  token: string
  undertaker_id?: string
  chain_id?: string
  entity_name: string
  entity_type: 'chain' | 'undertaker'
  entity_email?: string
  entity_city?: string
  entity_slug?: string
  template_name: string
  email_sent_at?: string
  submitted_at?: string
  resend_email_id?: string
  resend_last_event?: string
  status: string  // pending, sent, delivered, opened, clicked, bounced, complained, completed
  is_blacklisted?: boolean
  admin_notes?: string
  notes_updated_at?: string
}

interface EmailTemplate {
  id: string
  name: string
  subject: string
  html_body: string
  plain_text_body?: string
}

// Helper: Check if a token is considered "completed" (submitted, approved, rejected, or pending_review)
const isTokenCompleted = (t: SubmissionToken): boolean => {
  const completedStatuses = ['completed', 'approved', 'rejected', 'pending_review']
  return completedStatuses.includes(t.status) || !!t.submitted_at
}

export default function ServicesSubmissionManager() {
  const [tokens, setTokens] = useState<SubmissionToken[]>([])
  const [entities, setEntities] = useState<EntityOption[]>([])
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([])
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false)
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<ServicesTemplate | null>(null)
  const [generating, setGenerating] = useState(false)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [tabValue, setTabValue] = useState(0)
  const [dialogTab, setDialogTab] = useState(0) // 0 = generate link, 1 = send email
  
  // Bulk selection state
  const [bulkSelection, setBulkSelection] = useState<Set<string>>(new Set())
  const [bulkType, setBulkType] = useState<'chain' | 'undertaker'>('chain')
  const [bulkMode, setBulkMode] = useState<'generate' | 'send'>('send') // Default to send emails
  const [bulkDateFilter, setBulkDateFilter] = useState<string>('') // Filter by last email date
  const [bulkSendProgress, setBulkSendProgress] = useState({ current: 0, total: 0, sending: false })
  
  // Daily email limit tracking
  const [emailsSentToday, setEmailsSentToday] = useState(0)
  const remainingEmails = DAILY_EMAIL_LIMIT - emailsSentToday

  // Status filter and resend selection
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [resendSelection, setResendSelection] = useState<Set<string>>(new Set())
  const [resendDialogOpen, setResendDialogOpen] = useState(false)
  const [resendTemplate, setResendTemplate] = useState<EmailTemplate | null>(null)
  
  // Search
  const [searchQuery, setSearchQuery] = useState('')
  
  // Sort
  const [sortField, setSortField] = useState<string>('created_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  
  // Delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [tokenToDelete, setTokenToDelete] = useState<SubmissionToken | null>(null)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  
  // Row menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [menuToken, setMenuToken] = useState<SubmissionToken | null>(null)
  
  // Blacklist
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false)
  const [tokenToBlacklist, setTokenToBlacklist] = useState<SubmissionToken | null>(null)
  const [blacklistReason, setBlacklistReason] = useState('unsubscribed')
  const [blacklistNotes, setBlacklistNotes] = useState('')
  
  // Notes
  const [noteDialogOpen, setNoteDialogOpen] = useState(false)
  const [tokenToAddNote, setTokenToAddNote] = useState<SubmissionToken | null>(null)
  const [noteText, setNoteText] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  
  // Details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [tokenDetails, setTokenDetails] = useState<SubmissionToken | null>(null)
  
  // Confirmation dialogs
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false)
  const [resendStep, setResendStep] = useState<'select' | 'confirm'>('select')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      
      // Fetch all data in parallel
      const [tokensRes, undertakersRes, chainsRes, templatesRes, emailTemplatesRes] = await Promise.all([
        supabase
          .from('submission_tokens_with_details')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('undertakers')
          .select('id, name, email, city, phone, address, chain_id')
          .eq('published', true)
          .order('name'),
        supabase
          .from('chains')
          .select('id, name, slug, email, phone')
          .eq('published', true)
          .order('name'),
        supabase
          .from('services_templates')
          .select('*')
          .eq('is_active', true),
        supabase
          .from('email_templates')
          .select('*')
          .eq('is_active', true)
          .order('is_default', { ascending: false })
      ])

      if (tokensRes.error) throw tokensRes.error
      if (undertakersRes.error) throw undertakersRes.error
      if (chainsRes.error) throw chainsRes.error
      if (templatesRes.error) throw templatesRes.error
      // Don't throw on email templates - might not exist yet

      // Cast to our interface to avoid Supabase generated type conflicts
      const tokensList = (tokensRes.data || []) as SubmissionToken[]
      setTokens(tokensList)
      
      // Build a map of last email sent dates per entity
      const lastEmailMap: Record<string, string> = {}
      tokensList.forEach(token => {
        if (token.email_sent_at) {
          const entityKey = token.chain_id || token.undertaker_id
          if (entityKey) {
            const existing = lastEmailMap[entityKey]
            if (!existing || new Date(token.email_sent_at) > new Date(existing)) {
              lastEmailMap[entityKey] = token.email_sent_at
            }
          }
        }
      })
      
      // Combine chains and standalone undertakers into one list
      const chainEntities: EntityOption[] = (chainsRes.data || []).map(c => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
        email: c.email,
        phone: c.phone,
        type: 'chain' as const,
        last_email_sent_at: lastEmailMap[c.id]
      }))
      
      // Only include undertakers that are NOT part of a chain
      // Note: Undertakers don't have slugs - profile links only work for chains
      const undertakerEntities: EntityOption[] = (undertakersRes.data || [])
        .filter(u => !u.chain_id)
        .map(u => ({
          id: u.id,
          name: u.name,
          email: u.email,
          city: u.city,
          phone: u.phone,
          address: u.address,
          type: 'undertaker' as const,
          last_email_sent_at: lastEmailMap[u.id]
        }))
      
      setEntities([...chainEntities, ...undertakerEntities])
      setEmailTemplates(emailTemplatesRes.data || [])
      
      // Auto-select first active template
      if (templatesRes.data && templatesRes.data.length > 0) {
        setSelectedTemplate(templatesRes.data[0])
      }
      
      // Auto-select default email template
      if (emailTemplatesRes.data && emailTemplatesRes.data.length > 0) {
        setSelectedEmailTemplate(emailTemplatesRes.data[0])
      }
      
      // Note: Daily email counting removed - email_sends table not implemented
      // If needed in future, create email_sends table to track sent emails
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateToken = async () => {
    if (!selectedEntity || !selectedTemplate) return

    try {
      setGenerating(true)
      setError(null)

      const { data: { user } } = await supabase.auth.getUser()

      const { data, error } = await supabase
        .rpc('generate_submission_token', {
          p_undertaker_id: selectedEntity.type === 'undertaker' ? selectedEntity.id : null,
          p_chain_id: selectedEntity.type === 'chain' ? selectedEntity.id : null,
          p_template_id: selectedTemplate.id,
          p_created_by: user?.id
        })

      if (error) throw error

      const token = data as string
      const link = `${window.location.origin}/udfyld-services/${token}`

      // Copy to clipboard
      await navigator.clipboard.writeText(link)

      setSuccess(`✅ Link genereret og kopieret! Du kan nu sende den til ${selectedEntity.name}`)
      
      // Refresh tokens list
      await fetchData()
      
      // Close dialog
      setSendDialogOpen(false)
      setSelectedEntity(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setGenerating(false)
    }
  }

  // Generate WordPress-style slug from name
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'o')
      .replace(/å/g, 'aa')
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove remaining diacritics
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Remove multiple hyphens
      .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
  }

  // Replace variables in email template
  const replaceEmailVariables = (text: string, entity: EntityOption, link: string): string => {
    // Build profile link - use slug if available, otherwise generate from name
    const slug = entity.slug || slugify(entity.name)
    const profileLink = `https://find-bedemand.dk/bedemand/${slug}/`
    
    return text
      .replace(/\{\{virksomhedsnavn\}\}/g, entity.name)
      .replace(/\{\{email\}\}/g, entity.email || '')
      .replace(/\{\{link\}\}/g, link)
      .replace(/\{\{by\}\}/g, entity.city || '')
      .replace(/\{\{telefon\}\}/g, entity.phone || '')
      .replace(/\{\{adresse\}\}/g, entity.address || '')
      .replace(/\{\{profil_link\}\}/g, profileLink)
  }

  // Send email with link
  const handleSendEmail = async () => {
    console.log('🚀 handleSendEmail called')
    console.log('  selectedEntity:', selectedEntity)
    console.log('  selectedTemplate:', selectedTemplate)
    console.log('  selectedEmailTemplate:', selectedEmailTemplate)
    
    if (!selectedEntity || !selectedTemplate || !selectedEmailTemplate) {
      console.log('❌ Missing required data, returning early')
      return
    }
    if (!selectedEntity.email) {
      setError('Modtageren har ingen email adresse!')
      return
    }
    if (remainingEmails <= 0) {
      setError(`Du har nået den daglige grænse på ${DAILY_EMAIL_LIMIT} emails. Prøv igen i morgen.`)
      return
    }

    try {
      setSendingEmail(true)
      setError(null)
      console.log('📧 Starting email send process...')

      const { data: { user } } = await supabase.auth.getUser()

      // First generate the token
      const { data: tokenData, error: tokenError } = await supabase
        .rpc('generate_submission_token', {
          p_undertaker_id: selectedEntity.type === 'undertaker' ? selectedEntity.id : null,
          p_chain_id: selectedEntity.type === 'chain' ? selectedEntity.id : null,
          p_template_id: selectedTemplate.id,
          p_created_by: user?.id
        })

      if (tokenError) throw tokenError

      const token = tokenData as string
      const link = `${window.location.origin}/udfyld-services/${token}`

      // Replace variables in email
      const subject = replaceEmailVariables(selectedEmailTemplate.subject, selectedEntity, link)
      const html = replaceEmailVariables(selectedEmailTemplate.html_body, selectedEntity, link)
      const text = selectedEmailTemplate.plain_text_body 
        ? replaceEmailVariables(selectedEmailTemplate.plain_text_body, selectedEntity, link)
        : undefined

      // Send email via Edge Function
      const response = await fetch(`${ENV.SUPABASE_URL}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await functionAuthHeaders()),
        },
        body: JSON.stringify({
          to: selectedEntity.email,
          subject,
          html,
          text,
          submissionToken: token,  // Pass token to link email with submission
        }),
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to send email')
      }

      setSuccess(`Email sendt til ${selectedEntity.name}`)
      
      // Refresh tokens list
      await fetchData()
      
      // Close dialog
      setSendDialogOpen(false)
      setSelectedEntity(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSendingEmail(false)
    }
  }

  const handleBulkGenerate = async () => {
    if (bulkSelection.size === 0 || !selectedTemplate) return

    try {
      setGenerating(true)
      setError(null)

      const { data: { user } } = await supabase.auth.getUser()
      const links: string[] = []

      for (const entityId of Array.from(bulkSelection)) {
        const entity = entities.find(e => e.id === entityId)
        if (!entity) continue

        const { data, error } = await supabase
          .rpc('generate_submission_token', {
            p_undertaker_id: entity.type === 'undertaker' ? entity.id : null,
            p_chain_id: entity.type === 'chain' ? entity.id : null,
            p_template_id: selectedTemplate.id,
            p_created_by: user?.id
          })

        if (error) {
          console.error(`Failed for ${entity.name}:`, error)
          continue
        }

        const link = `${window.location.origin}/udfyld-services/${data}`
        links.push(`${entity.name}: ${link}`)
      }

      // Copy all links to clipboard
      await navigator.clipboard.writeText(links.join('\n'))

      setSuccess(`✅ ${links.length} links genereret og kopieret!`)
      
      // Refresh and close
      await fetchData()
      setBulkDialogOpen(false)
      setBulkSelection(new Set())
    } catch (err: any) {
      setError(err.message)
    } finally {
      setGenerating(false)
    }
  }

  const handleBulkSendEmail = async () => {
    if (bulkSelection.size === 0 || !selectedTemplate || !selectedEmailTemplate) return
    
    // Filter to only entities with email addresses
    const entitiesToSend = Array.from(bulkSelection)
      .map(id => entities.find(e => e.id === id))
      .filter((e): e is EntityOption => e !== undefined && !!e.email)
      .slice(0, remainingEmails)
    
    if (entitiesToSend.length === 0) {
      setError('Ingen af de valgte har email-adresser')
      return
    }

    try {
      setBulkSendProgress({ current: 0, total: entitiesToSend.length, sending: true })
      setError(null)

      const { data: { user } } = await supabase.auth.getUser()
      let successCount = 0
      let failedCount = 0

      for (let i = 0; i < entitiesToSend.length; i++) {
        const entity = entitiesToSend[i]
        setBulkSendProgress({ current: i + 1, total: entitiesToSend.length, sending: true })

        try {
          // Generate token
          const { data: tokenData, error: tokenError } = await supabase
            .rpc('generate_submission_token', {
              p_undertaker_id: entity.type === 'undertaker' ? entity.id : null,
              p_chain_id: entity.type === 'chain' ? entity.id : null,
              p_template_id: selectedTemplate.id,
              p_created_by: user?.id
            })

          if (tokenError) {
            console.error(`Token generation failed for ${entity.name}:`, tokenError)
            failedCount++
            continue
          }

          const token = tokenData as string
          const link = `${window.location.origin}/udfyld-services/${token}`

          // Replace variables in email
          const subject = replaceEmailVariables(selectedEmailTemplate.subject, entity, link)
          const html = replaceEmailVariables(selectedEmailTemplate.html_body, entity, link)
          const text = selectedEmailTemplate.plain_text_body 
            ? replaceEmailVariables(selectedEmailTemplate.plain_text_body, entity, link)
            : undefined

          // Send email via Edge Function
          const response = await fetch(`${ENV.SUPABASE_URL}/functions/v1/send-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(await functionAuthHeaders()),
            },
            body: JSON.stringify({
              to: entity.email,
              subject,
              html,
              text,
              submissionToken: token,
            }),
          })

          const result = await response.json()

          if (!result.success) {
            console.error(`Email failed for ${entity.name}:`, result.error)
            failedCount++
          } else {
            successCount++
          }
        } catch (err) {
          console.error(`Error processing ${entity.name}:`, err)
          failedCount++
        }
      }

      if (failedCount > 0) {
        setSuccess(`✅ ${successCount} emails sendt, ${failedCount} fejlede`)
      } else {
        setSuccess(`✅ ${successCount} emails sendt succesfuldt!`)
      }
      
      // Refresh and close
      await fetchData()
      setBulkDialogOpen(false)
      setBulkSelection(new Set())
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBulkSendProgress({ current: 0, total: 0, sending: false })
    }
  }

  const copyLink = async (token: string) => {
    const link = `${window.location.origin}/udfyld-services/${token}`
    await navigator.clipboard.writeText(link)
    setSuccess('Link kopieret!')
    setTimeout(() => setSuccess(null), 2000)
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Udfyldt'
      case 'clicked': return 'Klikket'
      case 'opened': return 'Åbnet'
      case 'delivered': return 'Leveret'
      case 'sent': return 'Sendt'
      case 'bounced': return 'Bounced'
      case 'complained': return 'Klaget'
      default: return 'Afventer'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#2e7d32'  // Green
      case 'clicked': return '#1565c0'    // Blue
      case 'opened': return '#1565c0'     // Blue
      case 'delivered': return '#666'     // Gray
      case 'sent': return '#999'          // Light gray
      case 'bounced': return '#d32f2f'    // Red
      case 'complained': return '#ed6c02' // Orange
      default: return '#999'
    }
  }

  // Refresh email statuses from Resend
  const handleRefreshStatus = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${ENV.SUPABASE_URL}/functions/v1/fetch-email-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await functionAuthHeaders()),
        },
      })
      const result = await response.json()
      if (result.success) {
        setSuccess(`Status opdateret for ${result.updated} emails`)
        await fetchData()
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const filteredTokens = tokens.filter(t => {
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      const matchesName = t.entity_name?.toLowerCase().includes(query)
      const matchesEmail = t.entity_email?.toLowerCase().includes(query)
      const matchesToken = t.token?.toLowerCase().includes(query)
      if (!matchesName && !matchesEmail && !matchesToken) return false
    }
    
    // Type filter
    if (tabValue === 1 && t.entity_type !== 'chain') return false
    if (tabValue === 2 && t.entity_type !== 'undertaker') return false
    
    // Status filter using helper function
    const isCompleted = isTokenCompleted(t)
    const hasOpened = t.status === 'opened' || t.status === 'clicked'
    
    if (statusFilter === 'not_completed') return !isCompleted
    if (statusFilter === 'not_responded') return !isCompleted && !hasOpened
    if (statusFilter === 'completed') return isCompleted
    if (statusFilter === 'opened') return hasOpened && !isCompleted
    if (statusFilter === 'sent') return t.email_sent_at && !isCompleted
    if (statusFilter === 'bounced') return t.status === 'bounced' || t.status === 'complained'
    if (statusFilter === 'blacklisted') return t.is_blacklisted
    
    return true
  })

  // Sort handler
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Sorted tokens
  const sortedTokens = [...filteredTokens].sort((a, b) => {
    let aVal: any = a[sortField as keyof SubmissionToken]
    let bVal: any = b[sortField as keyof SubmissionToken]
    
    // Handle null/undefined
    if (aVal == null) aVal = ''
    if (bVal == null) bVal = ''
    
    // Special handling for status (sort by priority)
    if (sortField === 'status') {
      const statusOrder = ['pending', 'sent', 'delivered', 'opened', 'clicked', 'bounced', 'complained', 'completed']
      aVal = statusOrder.indexOf(aVal)
      bVal = statusOrder.indexOf(bVal)
    }
    
    // Compare
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  // Sortable header component
  const SortableHeader = ({ field, label }: { field: string; label: string }) => (
    <TableCell
      sx={{ 
        fontWeight: 500, 
        color: '#666', 
        fontSize: '12px', 
        textTransform: 'uppercase', 
        letterSpacing: '0.5px',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: '#f5f5f5' }
      }}
      onClick={() => handleSort(field)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        {sortField === field && (
          sortDirection === 'asc' 
            ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
            : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
        )}
      </Box>
    </TableCell>
  )

  // Toggle selection for resend
  const toggleResendSelection = (tokenId: string) => {
    setResendSelection(prev => {
      const newSet = new Set(prev)
      if (newSet.has(tokenId)) {
        newSet.delete(tokenId)
      } else {
        newSet.add(tokenId)
      }
      return newSet
    })
  }

  // Select all visible tokens for resend
  const selectAllForResend = () => {
    const selectableTokens = sortedTokens.filter(t => !isTokenCompleted(t))
    if (resendSelection.size === selectableTokens.length) {
      setResendSelection(new Set())
    } else {
      setResendSelection(new Set(selectableTokens.map(t => t.id)))
    }
  }

  // Handle resend to selected
  const handleResendToSelected = async () => {
    if (!resendTemplate || resendSelection.size === 0) return

    try {
      setSendingEmail(true)
      setError(null)
      
      const selectedTokens = tokens.filter(t => resendSelection.has(t.id))
      let successCount = 0

      for (const token of selectedTokens) {
        if (successCount >= remainingEmails) {
          setError(`Daglig grænse nået. ${selectedTokens.length - successCount} emails ikke sendt.`)
          break
        }

        const link = `${window.location.origin}/udfyld-services/${token.token}`
        const slug = token.entity_slug || slugify(token.entity_name || '')
        const profileLink = slug ? `https://find-bedemand.dk/bedemand/${slug}/` : ''
        const html = resendTemplate.html_body
          .replace(/\{\{virksomhedsnavn\}\}/g, token.entity_name || '')
          .replace(/\{\{email\}\}/g, token.entity_email || '')
          .replace(/\{\{link\}\}/g, link)
          .replace(/\{\{profil_link\}\}/g, profileLink)
          .replace(/\{\{adresse\}\}/g, '')
          .replace(/\{\{by\}\}/g, token.entity_city || '')
          .replace(/\{\{telefon\}\}/g, '')

        const subject = resendTemplate.subject
          .replace(/\{\{virksomhedsnavn\}\}/g, token.entity_name || '')

        try {
          const response = await fetch(`${ENV.SUPABASE_URL}/functions/v1/send-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(await functionAuthHeaders()),
            },
            body: JSON.stringify({
              to: token.entity_email,
              toName: token.entity_name,
              subject,
              html,
              templateId: resendTemplate.id,
              submissionToken: token.token,
            }),
          })
          
          if (response.ok) {
            successCount++
          }
        } catch (e) {
          console.error('Error sending email:', e)
        }
      }

      setSuccess(`${successCount} påmindelses-emails sendt`)
      setResendSelection(new Set())
      setResendDialogOpen(false)
      setResendStep('select')
      setResendTemplate(null)
      await fetchData()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSendingEmail(false)
    }
  }

  // Helper functions
  const handleMarkAsCompleted = async (token: SubmissionToken) => {
    try {
      const { error } = await supabase.rpc('update_token_status', {
        p_token: token.token,
        p_status: 'submitted'
      })
      
      if (error) throw error
      
      setSuccess(`${token.entity_name} er markeret som udfyldt`)
      await fetchData()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleResetExpiry = async (token: SubmissionToken) => {
    try {
      const { data, error } = await supabase.rpc('reset_token_expiry', {
        p_token_id: token.id,
        p_days: 365
      })

      if (error) throw error
      if (!data?.success) throw new Error(data?.error || 'Ukendt fejl')

      setSuccess(`Udløbsdato for ${token.entity_name} er forlænget med 365 dage`)
      await fetchData()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleDeleteToken = async () => {
    if (!tokenToDelete) return
    
    try {
      setDeleting(true)
      setError(null)
      
      const { error } = await supabase.rpc('delete_submission_token', {
        p_token_id: tokenToDelete.id
      })
      
      if (error) throw error
      
      setSuccess(`Token for ${tokenToDelete.entity_name} er slettet`)
      setDeleteDialogOpen(false)
      setTokenToDelete(null)
      
      await fetchData()
    } catch (err: any) {
      setError(`Kunne ikke slette: ${err.message}`)
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (resendSelection.size === 0) return
    
    try {
      setDeleting(true)
      setError(null)
      
      const idsToDelete = Array.from(resendSelection)
      
      const { data, error } = await supabase.rpc('delete_submission_tokens', {
        p_token_ids: idsToDelete
      })
      
      if (error) throw error
      
      setSuccess(`${data} tokens er slettet`)
      setBulkDeleteDialogOpen(false)
      setResendSelection(new Set())
      
      await fetchData()
    } catch (err: any) {
      setError(`Kunne ikke slette: ${err.message}`)
    } finally {
      setDeleting(false)
    }
  }

  const handleAddToBlacklist = async () => {
    if (!tokenToBlacklist?.entity_email) return
    
    try {
      const { error } = await supabase.rpc('add_to_blacklist', {
        p_email: tokenToBlacklist.entity_email,
        p_reason: blacklistReason,
        p_notes: blacklistNotes || null
      })
      
      if (error) throw error
      
      setSuccess(`${tokenToBlacklist.entity_email} er tilføjet til blacklist`)
      setBlacklistDialogOpen(false)
      setTokenToBlacklist(null)
      setBlacklistReason('unsubscribed')
      setBlacklistNotes('')
      
      await fetchData()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleSaveNote = async () => {
    if (!tokenToAddNote) return
    
    try {
      setSavingNote(true)
      
      const { error } = await supabase.rpc('update_token_notes', {
        p_token_id: tokenToAddNote.id,
        p_notes: noteText || null
      })
      
      if (error) throw error
      
      setSuccess('Note gemt')
      setNoteDialogOpen(false)
      setTokenToAddNote(null)
      setNoteText('')
      
      await fetchData()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSavingNote(false)
    }
  }

  const chains = entities.filter(e => e.type === 'chain')
  const undertakers = entities.filter(e => e.type === 'undertaker')

  // Filter entities based on date filter
  const getFilteredEntities = (entityList: EntityOption[]) => {
    if (!bulkDateFilter) return entityList
    
    const now = new Date()
    
    return entityList.filter(entity => {
      if (bulkDateFilter === 'never') {
        return !entity.last_email_sent_at
      }
      
      const days = parseInt(bulkDateFilter)
      if (isNaN(days)) return true
      
      if (!entity.last_email_sent_at) return true // Never contacted = matches "not contacted in X days"
      
      const lastSent = new Date(entity.last_email_sent_at)
      const diffDays = Math.floor((now.getTime() - lastSent.getTime()) / (1000 * 60 * 60 * 24))
      return diffDays >= days
    })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            E-mail System
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Send links til bedemænd og kæder så de kan udfylde deres service-oplysninger
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshStatus}
            sx={{ color: '#666' }}
          >
            Hent Status
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setBulkDialogOpen(true)}
            sx={{ borderColor: '#ddd', color: '#333' }}
          >
            Bulk Send
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<SendIcon />}
            onClick={() => setSendDialogOpen(true)}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
          >
            Send Nyt Link
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics */}
      {(() => {
        const emailsSent = tokens.filter(t => t.email_sent_at).length
        const completed = tokens.filter(t => isTokenCompleted(t)).length
        const clicked = tokens.filter(t => t.status === 'clicked').length
        const opened = tokens.filter(t => t.status === 'opened').length
        const delivered = tokens.filter(t => t.status === 'delivered' || t.status === 'sent').length
        const contactedEntityIds = new Set(tokens.map(t => t.undertaker_id || t.chain_id))
        const notContacted = entities.filter(e => !contactedEntityIds.has(e.id)).length
        const responseRate = emailsSent > 0 ? Math.round((completed / emailsSent) * 100) : 0

        const StatCard = ({ value, label, accent }: { value: number | string, label: string, accent?: boolean }) => (
          <Box sx={{ 
            flex: '1 1 100px', 
            minWidth: 80,
            p: 2,
            bgcolor: accent ? '#f8f9fa' : 'white',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '9px' }}>
              {label}
            </Typography>
          </Box>
        )

        return (
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <StatCard value={emailsSent} label="Sendt" />
            <StatCard value={delivered} label="Leveret" />
            <StatCard value={opened} label="Åbnet" />
            <StatCard value={clicked} label="Klikket" />
            <StatCard value={completed} label="Udfyldt" accent />
            <StatCard value={notContacted} label="Ikke kontaktet" />
            <StatCard value={`${responseRate}%`} label="Svarprocent" accent />
          </Box>
        )
      })()}

      {/* Tabs */}
      <Tabs 
        value={tabValue} 
        onChange={(_, v) => setTabValue(v)} 
        sx={{ 
          mb: 2, 
          borderBottom: '1px solid #eee',
          '& .MuiTab-root': { 
            textTransform: 'none', 
            fontWeight: 500,
            minHeight: 40,
            fontSize: '13px',
          }
        }}
      >
        <Tab label={`Alle (${tokens.length})`} />
        <Tab label={`Kæder (${tokens.filter(t => t.entity_type === 'chain').length})`} />
        <Tab label={`Bedemænd (${tokens.filter(t => t.entity_type === 'undertaker').length})`} />
      </Tabs>

      {/* Search Input */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Søg efter navn, email eller token..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#999', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton 
                  size="small" 
                  onClick={() => setSearchQuery('')}
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {searchQuery && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {sortedTokens.length} resultater
          </Typography>
        )}
      </Box>

      {/* Filter and Resend Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontSize: '13px' }}>Filtrer på status</InputLabel>
          <Select
            value={statusFilter}
            label="Filtrer på status"
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ fontSize: '13px' }}
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="not_completed">Ikke udfyldt</MenuItem>
            <MenuItem value="not_responded">Ikke åbnet/klikket</MenuItem>
            <MenuItem value="sent">Sendt (afventer)</MenuItem>
            <MenuItem value="opened">Åbnet/klikket</MenuItem>
            <MenuItem value="completed">Udfyldt</MenuItem>
            <MenuItem value="bounced">Bounced/Klaget</MenuItem>
            <MenuItem value="blacklisted">Blacklistet</MenuItem>
          </Select>
        </FormControl>
        
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const selectableTokens = sortedTokens.filter(t => !isTokenCompleted(t))
            setResendSelection(new Set(selectableTokens.map(t => t.id)))
          }}
          disabled={sortedTokens.filter(t => !isTokenCompleted(t)).length === 0}
          sx={{ borderColor: '#1976d2', color: '#1976d2' }}
        >
          Vælg alle filtrerede ({sortedTokens.filter(t => !isTokenCompleted(t)).length})
        </Button>
        
        {resendSelection.size > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {resendSelection.size} valgt
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ReplayIcon />}
              onClick={() => setResendDialogOpen(true)}
              sx={{ borderColor: '#1565c0', color: '#1565c0' }}
            >
              Gensend til valgte
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setBulkDeleteDialogOpen(true)}
              sx={{ borderColor: '#d32f2f', color: '#d32f2f' }}
            >
              Slet valgte
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => setResendSelection(new Set())}
              sx={{ color: '#999' }}
            >
              Ryd
            </Button>
          </Box>
        )}
      </Box>

      {/* Tokens Table */}
      <TableContainer component={Paper} sx={{ border: '1px solid #eee', boxShadow: 'none' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell padding="checkbox" sx={{ width: 40 }}>
                <Checkbox
                  size="small"
                  checked={resendSelection.size > 0 && resendSelection.size === sortedTokens.filter(t => !isTokenCompleted(t)).length}
                  indeterminate={resendSelection.size > 0 && resendSelection.size < sortedTokens.filter(t => !isTokenCompleted(t)).length}
                  onChange={selectAllForResend}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#666', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</TableCell>
              <SortableHeader field="entity_name" label="Navn" />
              <SortableHeader field="entity_email" label="Email" />
              <SortableHeader field="status" label="Status" />
              <SortableHeader field="email_sent_at" label="Sendt" />
              <SortableHeader field="submitted_at" label="Udfyldt" />
              <TableCell sx={{ fontWeight: 500, color: '#666', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }} align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTokens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#999' }}>
                  Ingen links sendt endnu
                </TableCell>
              </TableRow>
            ) : (
              sortedTokens.map((token) => (
                <TableRow key={token.id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={resendSelection.has(token.id)}
                      onChange={() => toggleResendSelection(token.id)}
                      disabled={isTokenCompleted(token)}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="caption" sx={{ 
                      color: '#666', 
                      bgcolor: '#f5f5f5', 
                      px: 1, 
                      py: 0.25, 
                      borderRadius: 0.5,
                      fontSize: '11px'
                    }}>
                      {token.entity_type === 'chain' ? 'Kæde' : 'Bedemand'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontWeight: 500, fontSize: '13px' }}>{token.entity_name}</TableCell>
                  <TableCell sx={{ py: 1.5, color: '#666', fontSize: '13px' }}>
                    {token.entity_email || '–'}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ 
                        color: getStatusColor(token.status),
                        fontWeight: ['completed', 'clicked', 'opened', 'bounced', 'complained'].includes(token.status) ? 600 : 400,
                        fontSize: '12px'
                      }}>
                        {getStatusLabel(token.status)}
                      </Typography>
                      {token.is_blacklisted && (
                        <Tooltip title="Blacklistet - modtager ikke emails">
                          <BlockIcon sx={{ fontSize: 14, color: '#d32f2f' }} />
                        </Tooltip>
                      )}
                      {token.admin_notes && (
                        <Tooltip title={token.admin_notes}>
                          <NoteIcon 
                            sx={{ 
                              fontSize: 14, 
                              color: '#1976d2', 
                              cursor: 'pointer'
                            }} 
                            onClick={() => {
                              setTokenToAddNote(token)
                              setNoteText(token.admin_notes || '')
                              setNoteDialogOpen(true)
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, color: '#666', fontSize: '13px' }}>
                    {token.email_sent_at 
                      ? new Date(token.email_sent_at).toLocaleDateString('da-DK')
                      : '–'
                    }
                  </TableCell>
                  <TableCell sx={{ py: 1.5, color: '#666', fontSize: '13px' }}>
                    {token.submitted_at 
                      ? new Date(token.submitted_at).toLocaleDateString('da-DK')
                      : '–'
                    }
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        setMenuAnchorEl(event.currentTarget)
                        setMenuToken(token)
                      }}
                      sx={{ color: '#666' }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, borderBottom: '1px solid #eee' }}>
          <Tabs 
            value={dialogTab} 
            onChange={(_, v) => setDialogTab(v)}
            sx={{ 
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '13px' }
            }}
          >
            <Tab label="Generer Link" />
            <Tab label="Send Email" />
          </Tabs>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 3 }}>
            <Autocomplete
              fullWidth
              size="small"
              options={entities}
              groupBy={(option) => option.type === 'chain' ? 'Kæder' : 'Bedemænd'}
              getOptionLabel={(option) => option.city ? `${option.name} – ${option.city}` : option.name}
              value={selectedEntity}
              onChange={(_, newValue) => setSelectedEntity(newValue)}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ py: 1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.city}{option.email && ` · ${option.email}`}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Vælg modtager" size="small" sx={{ mb: 2 }} />
              )}
            />

            {selectedEntity && !selectedEntity.email && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                Ingen email registreret – kan ikke sende email
              </Typography>
            )}

            {dialogTab === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                Generer et unikt link som du selv kan sende til modtageren.
              </Typography>
            )}

            {dialogTab === 1 && (
              <Box>
                {remainingEmails < 20 && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 2 }}>
                    {remainingEmails} emails tilbage i dag
                  </Typography>
                )}

                <Autocomplete
                  fullWidth
                  size="small"
                  options={emailTemplates}
                  getOptionLabel={(option) => option.name}
                  value={selectedEmailTemplate}
                  onChange={(_, newValue) => setSelectedEmailTemplate(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Email skabelon" size="small" sx={{ mb: 2 }} />
                  )}
                />

                {selectedEmailTemplate && selectedEntity && (
                  <Box sx={{ border: '1px solid #eee', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ bgcolor: '#fafafa', px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                      <Typography variant="caption" color="text.secondary">Til: {selectedEntity.email || '–'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                        {selectedEmailTemplate.subject.replace(/\{\{virksomhedsnavn\}\}/g, selectedEntity.name)}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ p: 2, maxHeight: 200, overflow: 'auto', fontSize: '12px', lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{
                        __html: selectedEmailTemplate.html_body
                          .replace(/\{\{virksomhedsnavn\}\}/g, selectedEntity.name)
                          .replace(/\{\{link\}\}/g, '<span style="color: #1565c0; text-decoration: underline">[LINK]</span>')
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #eee', px: 3, py: 2 }}>
          <Button 
            onClick={() => setSendDialogOpen(false)} 
            disabled={generating || sendingEmail}
            sx={{ color: '#666' }}
          >
            Annuller
          </Button>
          
          {dialogTab === 0 ? (
            <Button
              onClick={handleGenerateToken}
              variant="contained"
              size="small"
              disabled={!selectedEntity || !selectedTemplate || generating}
              startIcon={generating ? <CircularProgress size={16} /> : null}
              sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
            >
              {generating ? 'Genererer...' : 'Generer Link'}
            </Button>
          ) : (
            <Button
              onClick={() => setSendConfirmOpen(true)}
              variant="contained"
              size="small"
              disabled={!selectedEntity || !selectedTemplate || !selectedEmailTemplate || !selectedEntity?.email || sendingEmail}
              startIcon={sendingEmail ? <CircularProgress size={16} /> : null}
              sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
            >
              {sendingEmail ? 'Sender...' : 'Send Email'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => !bulkSendProgress.sending && setBulkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Bulk Send</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 3 }}>
            {/* Mode tabs: Send Email vs Generate Links */}
            <Tabs 
              value={bulkMode === 'send' ? 0 : 1} 
              onChange={(_, v) => setBulkMode(v === 0 ? 'send' : 'generate')}
              sx={{ 
                mb: 3, 
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '13px' }
              }}
            >
              <Tab label="📧 Send Emails" />
              <Tab label="🔗 Kun Generer Links" />
            </Tabs>

            {/* Email template selection for send mode */}
            {bulkMode === 'send' && (
              <Autocomplete
                options={emailTemplates}
                getOptionLabel={(option) => option.name}
                value={selectedEmailTemplate}
                onChange={(_, value) => setSelectedEmailTemplate(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Vælg email-skabelon" size="small" />
                )}
                sx={{ mb: 3 }}
              />
            )}

            {/* Entity type tabs */}
            <Tabs 
              value={bulkType === 'chain' ? 0 : 1} 
              onChange={(_, v) => {
                setBulkType(v === 0 ? 'chain' : 'undertaker')
                setBulkSelection(new Set())
              }} 
              sx={{ 
                mb: 2, 
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '13px' }
              }}
            >
              <Tab label={`Kæder (${chains.length})`} />
              <Tab label={`Bedemænd (${undertakers.length})`} />
            </Tabs>

            {/* Date filter */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filtrer efter sidste email</InputLabel>
                <Select
                  value={bulkDateFilter}
                  onChange={(e) => {
                    setBulkDateFilter(e.target.value)
                    setBulkSelection(new Set())
                  }}
                  label="Filtrer efter sidste email"
                >
                  <MenuItem value="">Alle</MenuItem>
                  <MenuItem value="never">Aldrig kontaktet</MenuItem>
                  <MenuItem value="7">Ikke kontaktet i 7+ dage</MenuItem>
                  <MenuItem value="14">Ikke kontaktet i 14+ dage</MenuItem>
                  <MenuItem value="30">Ikke kontaktet i 30+ dage</MenuItem>
                  <MenuItem value="60">Ikke kontaktet i 60+ dage</MenuItem>
                  <MenuItem value="90">Ikke kontaktet i 90+ dage</MenuItem>
                </Select>
              </FormControl>
              {bulkDateFilter && (
                <Button size="small" onClick={() => setBulkDateFilter('')} sx={{ color: '#666' }}>
                  Nulstil filter
                </Button>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '13px' }}>
              {bulkMode === 'send' 
                ? `Vælg de ${bulkType === 'chain' ? 'kæder' : 'bedemænd'} du vil sende emails til.`
                : `Vælg de ${bulkType === 'chain' ? 'kæder' : 'bedemænd'} du vil generere links til.`
              }
            </Typography>

            {bulkMode === 'send' && bulkSelection.size > remainingEmails && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Du har valgt {bulkSelection.size}, men kan kun sende {remainingEmails} emails i dag.
              </Alert>
            )}

            {bulkSendProgress.sending && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Sender emails... {bulkSendProgress.current} af {bulkSendProgress.total}
              </Alert>
            )}

            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button 
                size="small" 
                variant="text"
                sx={{ fontSize: '12px', color: '#666' }}
                onClick={() => {
                  const filtered = getFilteredEntities(bulkType === 'chain' ? chains : undertakers)
                    .filter(e => e.email)
                    .slice(0, bulkMode === 'send' ? remainingEmails : 1000)
                    .map(e => e.id)
                  setBulkSelection(new Set(filtered))
                }}
              >
                Vælg alle med email {bulkMode === 'send' && `(max ${remainingEmails})`}
              </Button>
              <Button 
                size="small" 
                variant="text"
                sx={{ fontSize: '12px', color: '#666' }}
                onClick={() => setBulkSelection(new Set())}
              >
                Fravælg alle
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
                {bulkSelection.size} valgt
              </Typography>
            </Box>

            <Box sx={{ border: '1px solid #eee', borderRadius: 1, maxHeight: 350, overflow: 'auto' }}>
              {getFilteredEntities(bulkType === 'chain' ? chains : undertakers).map((entity, index, arr) => (
                <Box 
                  key={entity.id}
                  sx={{ 
                    px: 2, 
                    py: 1,
                    borderBottom: index < arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                    '&:hover': { bgcolor: '#fafafa' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    opacity: !entity.email ? 0.5 : 1
                  }}
                  onClick={() => {
                    const newSelection = new Set(bulkSelection)
                    if (newSelection.has(entity.id)) {
                      newSelection.delete(entity.id)
                    } else {
                      newSelection.add(entity.id)
                    }
                    setBulkSelection(newSelection)
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={bulkSelection.has(entity.id)}
                    disabled={!entity.email && bulkMode === 'send'}
                    sx={{ 
                      '&.Mui-checked': { color: '#1a1a1a' },
                      pointerEvents: 'none'
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                      {entity.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {entity.email || 'Ingen email'}
                        {!entity.email && (
                          <span style={{ color: '#d32f2f', marginLeft: 4 }}>⚠️</span>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        bgcolor: entity.last_email_sent_at ? '#e8f5e9' : '#fff3e0',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '11px'
                      }}>
                        {entity.last_email_sent_at 
                          ? `Senest: ${new Date(entity.last_email_sent_at).toLocaleDateString('da-DK')}`
                          : 'Aldrig kontaktet'
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
              {getFilteredEntities(bulkType === 'chain' ? chains : undertakers).length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Ingen {bulkType === 'chain' ? 'kæder' : 'bedemænd'} matcher filteret
                  </Typography>
                </Box>
              )}
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #eee', px: 3, py: 2 }}>
          <Button 
            onClick={() => setBulkDialogOpen(false)} 
            disabled={bulkSendProgress.sending} 
            sx={{ color: '#666' }}
          >
            Annuller
          </Button>
          {bulkMode === 'generate' ? (
            <Button
              onClick={handleBulkGenerate}
              variant="contained"
              size="small"
              disabled={bulkSelection.size === 0 || !selectedTemplate || generating}
              startIcon={generating ? <CircularProgress size={16} /> : null}
              sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
            >
              {generating ? 'Genererer...' : `Generer ${bulkSelection.size} Links`}
            </Button>
          ) : (
            <Button
              onClick={handleBulkSendEmail}
              variant="contained"
              size="small"
              disabled={
                bulkSelection.size === 0 || 
                !selectedTemplate || 
                !selectedEmailTemplate || 
                bulkSendProgress.sending
              }
              startIcon={bulkSendProgress.sending ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              {bulkSendProgress.sending 
                ? `Sender ${bulkSendProgress.current}/${bulkSendProgress.total}...`
                : `Send ${Math.min(bulkSelection.size, remainingEmails)} Emails`
              }
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Resend Dialog */}
      <Dialog open={resendDialogOpen} onClose={() => {
        setResendDialogOpen(false)
        setResendStep('select')
        setResendTemplate(null)
      }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
          {resendStep === 'select' ? 'Gensend påmindelse' : 'Bekræft afsendelse'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {resendStep === 'select' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Send påmindelse til {resendSelection.size} modtagere som ikke har udfyldt deres services endnu.
              </Typography>

              <Autocomplete
                options={emailTemplates}
                getOptionLabel={(option) => option.name}
                value={resendTemplate}
                onChange={(_, value) => setResendTemplate(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Vælg email-skabelon" size="small" />
                )}
                sx={{ mb: 3 }}
              />

              {resendTemplate && (
                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #eee' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Forhåndsvisning af emne:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {resendTemplate.subject}
                  </Typography>
                </Box>
              )}

              {resendSelection.size > remainingEmails && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Du har valgt {resendSelection.size}, men kan kun sende {remainingEmails} emails i dag.
                </Alert>
              )}
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Du er ved at sende {Math.min(resendSelection.size, remainingEmails)} emails!
              </Alert>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Skabelon:</strong> {resendTemplate?.name}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>Modtagere:</Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                {tokens
                  .filter(t => resendSelection.has(t.id))
                  .slice(0, remainingEmails)
                  .map(t => (
                    <Typography key={t.id} variant="body2" sx={{ py: 0.5 }}>
                      • {t.entity_name} - {t.entity_email}
                    </Typography>
                  ))
                }
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #eee', px: 3, py: 2 }}>
          <Button onClick={() => {
            if (resendStep === 'confirm') {
              setResendStep('select')
            } else {
              setResendDialogOpen(false)
              setResendStep('select')
              setResendTemplate(null)
            }
          }} disabled={sendingEmail} sx={{ color: '#666' }}>
            {resendStep === 'confirm' ? 'Tilbage' : 'Annuller'}
          </Button>
          {resendStep === 'select' ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => setResendStep('confirm')}
              disabled={!resendTemplate}
              sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#1976d2' } }}
            >
              Fortsæt
            </Button>
          ) : (
            <Button
              onClick={handleResendToSelected}
              variant="contained"
              size="small"
              disabled={sendingEmail}
              startIcon={sendingEmail ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#c62828' } }}
            >
              {sendingEmail ? 'Sender...' : `Send ${Math.min(resendSelection.size, remainingEmails)} emails`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Row Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null)
          setMenuToken(null)
        }}
        PaperProps={{
          sx: { minWidth: 180, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
        }}
      >
        <MenuItem onClick={() => {
          if (menuToken) copyLink(menuToken.token)
          setMenuAnchorEl(null)
        }}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} />
          Kopier link
        </MenuItem>
        
        <MenuItem 
          onClick={() => {
            setResendSelection(new Set([menuToken!.id]))
            setResendDialogOpen(true)
            setMenuAnchorEl(null)
          }}
          disabled={menuToken ? isTokenCompleted(menuToken) : true}
        >
          <ReplayIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} />
          Gensend email
        </MenuItem>
        
        <MenuItem onClick={() => {
          setTokenDetails(menuToken)
          setDetailsDialogOpen(true)
          setMenuAnchorEl(null)
        }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} />
          Se detaljer
        </MenuItem>
        
        <Divider />
        
        <MenuItem 
          onClick={() => {
            handleMarkAsCompleted(menuToken!)
            setMenuAnchorEl(null)
          }}
          disabled={menuToken ? isTokenCompleted(menuToken) : true}
        >
          <CheckCircleIcon fontSize="small" sx={{ mr: 1.5, color: '#2e7d32' }} />
          Marker som udfyldt
        </MenuItem>
        
        <MenuItem 
          onClick={() => {
            handleResetExpiry(menuToken!)
            setMenuAnchorEl(null)
          }}
          disabled={menuToken ? isTokenCompleted(menuToken) : true}
        >
          <UpdateIcon fontSize="small" sx={{ mr: 1.5, color: '#1976d2' }} />
          Forlæng udløbsdato (365 dage)
        </MenuItem>
        
        <MenuItem onClick={() => {
          setTokenToAddNote(menuToken)
          setNoteText(menuToken?.admin_notes || '')
          setNoteDialogOpen(true)
          setMenuAnchorEl(null)
        }}>
          <NoteAddIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} />
          Tilføj note
        </MenuItem>
        
        <Divider />
        
        <MenuItem 
          onClick={() => {
            setTokenToBlacklist(menuToken)
            setBlacklistDialogOpen(true)
            setMenuAnchorEl(null)
          }}
          disabled={menuToken?.is_blacklisted}
        >
          <BlockIcon fontSize="small" sx={{ mr: 1.5, color: '#ed6c02' }} />
          Blacklist modtager
        </MenuItem>
        
        <MenuItem 
          onClick={() => {
            setTokenToDelete(menuToken)
            setDeleteDialogOpen(true)
            setMenuAnchorEl(null)
          }}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
          Slet
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ pb: 1 }}>
          Bekræft sletning
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Er du sikker på du vil slette dette token for <strong>{tokenToDelete?.entity_name}</strong>?
          </Typography>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            Dette kan ikke fortrydes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleting}
            sx={{ color: '#666' }}
          >
            Annuller
          </Button>
          <Button
            onClick={handleDeleteToken}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Sletter...' : 'Slet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Bekræft sletning af {resendSelection.size} tokens</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Du er ved at slette følgende tokens:
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
            {tokens
              .filter(t => resendSelection.has(t.id))
              .map(t => (
                <Typography key={t.id} variant="body2" sx={{ py: 0.5 }}>
                  • {t.entity_name} ({t.entity_email || 'ingen email'})
                </Typography>
              ))
            }
          </Box>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 2 }}>
            Dette kan ikke fortrydes!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkDeleteDialogOpen(false)} disabled={deleting}>
            Annuller
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Sletter...' : `Slet ${resendSelection.size} tokens`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blacklist Dialog */}
      <Dialog open={blacklistDialogOpen} onClose={() => setBlacklistDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tilføj til blacklist</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Tilføj <strong>{tokenToBlacklist?.entity_email}</strong> til blacklist.
            De vil ikke modtage flere emails fra systemet.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Årsag</InputLabel>
            <Select
              value={blacklistReason}
              label="Årsag"
              onChange={(e) => setBlacklistReason(e.target.value)}
            >
              <MenuItem value="unsubscribed">Ønsker ikke emails</MenuItem>
              <MenuItem value="bounced">Email bounced</MenuItem>
              <MenuItem value="complained">Klagede over spam</MenuItem>
              <MenuItem value="invalid">Ugyldig email</MenuItem>
              <MenuItem value="manual">Anden årsag</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Noter (valgfrit)"
            value={blacklistNotes}
            onChange={(e) => setBlacklistNotes(e.target.value)}
            placeholder="F.eks. 'Ringede og bad om at blive fjernet'"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBlacklistDialogOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleAddToBlacklist}
            startIcon={<BlockIcon />}
          >
            Tilføj til blacklist
          </Button>
        </DialogActions>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Noter for {tokenToAddNote?.entity_name}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Tilføj interne noter her...&#10;&#10;F.eks:&#10;- Ringet d. 15/1 - svarer næste uge&#10;- Har bedt om nyt link"
            sx={{ mt: 1 }}
          />
          {tokenToAddNote?.notes_updated_at && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Sidst opdateret: {new Date(tokenToAddNote.notes_updated_at).toLocaleString('da-DK')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setNoteDialogOpen(false)} disabled={savingNote}>
            Annuller
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSaveNote}
            disabled={savingNote}
            startIcon={savingNote ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingNote ? 'Gemmer...' : 'Gem note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee' }}>
          Token Detaljer
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {tokenDetails && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
                  Modtager Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Navn:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{tokenDetails.entity_name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Email:</Typography>
                      <Typography variant="body2">{tokenDetails.entity_email || '–'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Type:</Typography>
                      <Typography variant="body2">{tokenDetails.entity_type === 'chain' ? 'Kæde' : 'Bedemand'}</Typography>
                    </Box>
                    {tokenDetails.entity_city && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">By:</Typography>
                        <Typography variant="body2">{tokenDetails.entity_city}</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
                  Token Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: getStatusColor(tokenDetails.status) }}>
                        {getStatusLabel(tokenDetails.status)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Token:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '11px' }}>
                        {tokenDetails.token}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Skabelon:</Typography>
                      <Typography variant="body2">{tokenDetails.template_name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Link:</Typography>
                      <Button 
                        size="small" 
                        variant="text"
                        startIcon={<ContentCopyIcon fontSize="small" />}
                        onClick={() => copyLink(tokenDetails.token)}
                        sx={{ textTransform: 'none', fontSize: '12px' }}
                      >
                        Kopier link
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
                  Email Tracking
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Email sendt:</Typography>
                      <Typography variant="body2">
                        {tokenDetails.email_sent_at 
                          ? new Date(tokenDetails.email_sent_at).toLocaleString('da-DK')
                          : 'Ikke sendt'
                        }
                      </Typography>
                    </Box>
                    {tokenDetails.resend_email_id && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Resend ID:</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '11px' }}>
                          {tokenDetails.resend_email_id}
                        </Typography>
                      </Box>
                    )}
                    {tokenDetails.resend_last_event && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Sidste event:</Typography>
                        <Typography variant="body2">{tokenDetails.resend_last_event}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Udfyldt:</Typography>
                      <Typography variant="body2">
                        {tokenDetails.submitted_at 
                          ? new Date(tokenDetails.submitted_at).toLocaleString('da-DK')
                          : 'Ikke udfyldt'
                        }
                      </Typography>
                    </Box>
                    {tokenDetails.is_blacklisted && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Blacklistet:</Typography>
                        <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 600 }}>Ja</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>

              {tokenDetails.admin_notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
                    Admin Noter
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {tokenDetails.admin_notes}
                    </Typography>
                    {tokenDetails.notes_updated_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Opdateret: {new Date(tokenDetails.notes_updated_at).toLocaleString('da-DK')}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #eee', px: 3, py: 2 }}>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Luk
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog open={sendConfirmOpen} onClose={() => setSendConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f8f9fa' }}>
          Bekræft afsendelse
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Du er ved at sende en email til:
          </Typography>
          
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {selectedEntity?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedEntity?.email}
            </Typography>
          </Paper>
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Skabelon:</strong> {selectedEmailTemplate?.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Emne:</strong> {selectedEmailTemplate?.subject.replace(/\{\{virksomhedsnavn\}\}/g, selectedEntity?.name || '')}
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Emailen sendes med det samme og kan ikke fortrydes.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSendConfirmOpen(false)}>
            Annuller
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSendConfirmOpen(false)
              handleSendEmail()
            }}
            startIcon={<SendIcon />}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
          >
            Bekræft og send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
